import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLang } from '@/context/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public auto-scrolling marquee of "Ils nous font confiance" logos.
 * - Pulls config + items from /api/landing/trusted-logos
 * - Returns null if disabled or no logos (no visual residue)
 * - Grayscale by default, full color on hover
 * - Pauses on hover (UX courtoisie)
 */
export default function TrustedLogosMarquee() {
    const { lang } = useLang();
    const [state, setState] = useState({ enabled: false, items: [], title: '', subtitle: '' });

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/landing/trusted-logos`)
            .then((r) => {
                if (cancelled) return;
                const d = r.data || {};
                setState({
                    enabled: !!d.enabled,
                    items: Array.isArray(d.items) ? d.items : [],
                    title: lang === 'en' ? (d.title_en || 'They trust us') : (d.title_fr || 'Ils nous font confiance'),
                    subtitle: lang === 'en' ? (d.subtitle_en || '') : (d.subtitle_fr || ''),
                });
            })
            .catch(() => { /* graceful: keep disabled */ });
        return () => { cancelled = true; };
    }, [lang]);

    if (!state.enabled || state.items.length === 0) return null;

    // Duplicate the list so the marquee loops seamlessly
    const loop = [...state.items, ...state.items];

    return (
        <section
            id="trusted"
            aria-labelledby="trusted-title"
            className="py-16 lg:py-20 bg-background border-y border-border/40"
            data-testid="trusted-logos-section"
        >
            <div className="container-page text-center mb-10">
                <div className="eyebrow text-primary mb-3" data-testid="trusted-logos-eyebrow">
                    {lang === 'en' ? 'Trust & Proof' : 'Confiance & preuves'}
                </div>
                <h2 id="trusted-title" className="font-heading text-2xl lg:text-3xl text-foreground" data-testid="trusted-logos-title">
                    {state.title}
                </h2>
                {state.subtitle && (
                    <p className="mt-3 text-sm lg:text-base text-foreground/65 max-w-2xl mx-auto leading-relaxed">
                        {state.subtitle}
                    </p>
                )}
            </div>

            {/* Edge-to-edge marquee with side fades for premium feel */}
            <div className="relative overflow-hidden" data-testid="trusted-logos-marquee">
                {/* fade left */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10"
                    style={{ background: 'linear-gradient(90deg, var(--background, #FBF8F3) 0%, transparent 100%)' }}
                />
                {/* fade right */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10"
                    style={{ background: 'linear-gradient(270deg, var(--background, #FBF8F3) 0%, transparent 100%)' }}
                />

                <div className="trusted-marquee-track">
                    {loop.map((logo, idx) => {
                        const inner = (
                            <img
                                src={logo.logo_url}
                                alt={logo.name}
                                title={logo.name}
                                loading="lazy"
                                draggable={false}
                                className="h-12 lg:h-14 w-auto max-w-[180px] object-contain opacity-70 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                            />
                        );
                        const cls = "shrink-0 inline-flex items-center justify-center px-2";
                        return logo.link_url ? (
                            <a
                                key={`${logo.id}-${idx}`}
                                href={logo.link_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cls}
                                data-testid={`trusted-logo-${logo.id}`}
                            >
                                {inner}
                            </a>
                        ) : (
                            <div key={`${logo.id}-${idx}`} className={cls} data-testid={`trusted-logo-${logo.id}`}>
                                {inner}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
